import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, CartesianGrid,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ComposedChart, FunnelChart, Funnel, LabelList,
} from "recharts";
import {
  Sparkles, Activity, TrendingUp, TrendingDown, AlertTriangle,
  DollarSign, Stethoscope, Pill, Brain, Users, MapPin, Share2,
  Loader2, ChevronRight, ArrowUpRight, ArrowDownRight, Zap, Building2,
  FileText, ShieldAlert, Workflow,
} from "lucide-react";
import DashboardLayout, { useTheme } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { id: "executive", label: "Executive Summary", icon: Activity },
  { id: "denials", label: "Denial Analytics", icon: ShieldAlert },
  { id: "claims", label: "Claims Intelligence", icon: DollarSign },
  { id: "referrals", label: "Referral Analytics", icon: Share2 },
  { id: "pharmacy", label: "Pharmacy PA", icon: Pill },
  { id: "ai-predictions", label: "AI Predictions", icon: Brain },
  { id: "productivity", label: "Provider Productivity", icon: Stethoscope },
  { id: "enterprise", label: "Multi-Location Enterprise", icon: Building2 },
  { id: "npi-portability", label: "NPI Portability", icon: Workflow },
];

const PALETTE_DARK = ["#10b981", "#06b6d4", "#8b5cf6", "#f59e0b", "#ef4444", "#ec4899", "#3b82f6", "#84cc16"];
const PALETTE_LIGHT = ["#0066a1", "#10b981", "#7c3aed", "#f59e0b", "#dc2626", "#db2777", "#0891b2", "#65a30d"];

const fmtNumber = (n) => new Intl.NumberFormat("en-US").format(n || 0);
const fmtCurrency = (n) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", maximumFractionDigits: 0
}).format(n || 0);
const fmtPercent = (n) => `${(n ?? 0).toFixed(1)}%`;
const fmtHours = (n) => `${(n ?? 0).toFixed(1)}h`;

// ============ KPI Card ============
function KpiCard({ kpi, isDark, palette }) {
  const positive = kpi.delta_pct >= 0;
  const trendIcon = kpi.trend === "up"
    ? <ArrowUpRight className="w-3.5 h-3.5" />
    : <ArrowDownRight className="w-3.5 h-3.5" />;

  const valueDisplay = useMemo(() => {
    switch (kpi.format) {
      case "currency": return fmtCurrency(kpi.value);
      case "percent": return fmtPercent(kpi.value);
      case "hours": return fmtHours(kpi.value);
      default: return fmtNumber(kpi.value);
    }
  }, [kpi]);

  return (
    <Card className={isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-gray-200"}
      data-testid={`kpi-${kpi.key}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <span className={`text-xs uppercase tracking-wider font-semibold ${isDark ? "text-slate-400" : "text-gray-500"}`}>
            {kpi.label}
          </span>
          <span className={`flex items-center gap-1 text-xs font-medium ${positive ? "text-emerald-500" : "text-rose-500"}`}>
            {trendIcon}{Math.abs(kpi.delta_pct).toFixed(1)}%
          </span>
        </div>
        <div className={`text-3xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
          {valueDisplay}
        </div>
        <div className="mt-2 h-1.5 w-full rounded-full bg-slate-700/30 overflow-hidden">
          <div className="h-full rounded-full" style={{
            width: `${Math.min(Math.abs(kpi.delta_pct) * 4, 100)}%`,
            background: positive ? palette[0] : "#ef4444",
          }} />
        </div>
      </CardContent>
    </Card>
  );
}

// ============ Executive Summary ============
function ExecutiveSection({ data, isDark, palette }) {
  if (!data) return <SectionLoader />;
  const surfaceCard = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-gray-200";
  const textTitle = isDark ? "text-white" : "text-gray-900";
  const axisColor = isDark ? "#64748b" : "#475569";

  return (
    <div className="space-y-6" data-testid="section-executive">
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {data.kpis.map(k => <KpiCard key={k.key} kpi={k} isDark={isDark} palette={palette} />)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className={`${surfaceCard} lg:col-span-2`}>
          <CardHeader>
            <CardTitle className={`text-base ${textTitle}`}>PA Volume Trend — 12 months</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={data.volume_trend}>
                <defs>
                  <linearGradient id="gradSubmitted" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={palette[0]} stopOpacity={0.6} />
                    <stop offset="100%" stopColor={palette[0]} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1e293b" : "#e2e8f0"} />
                <XAxis dataKey="month" tick={{ fill: axisColor, fontSize: 11 }} />
                <YAxis tick={{ fill: axisColor, fontSize: 11 }} />
                <Tooltip contentStyle={{ background: isDark ? "#0f172a" : "#fff", border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }} />
                <Area type="monotone" dataKey="submitted" stroke={palette[0]} strokeWidth={2} fill="url(#gradSubmitted)" />
                <Line type="monotone" dataKey="approved" stroke={palette[1]} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="denied" stroke={palette[4]} strokeWidth={2} dot={false} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className={surfaceCard}>
          <CardHeader>
            <CardTitle className={`text-base ${textTitle}`}>Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={data.status_mix} dataKey="value" nameKey="name" cx="50%" cy="50%"
                  innerRadius={60} outerRadius={100} paddingAngle={3}>
                  {data.status_mix.map((_, i) => <Cell key={i} fill={palette[i % palette.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: isDark ? "#0f172a" : "#fff", border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card className={surfaceCard}>
        <CardHeader>
          <CardTitle className={`text-base ${textTitle}`}>Payer Performance Mix</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={data.payer_mix}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1e293b" : "#e2e8f0"} />
              <XAxis dataKey="payer" tick={{ fill: axisColor, fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fill: axisColor, fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: axisColor, fontSize: 11 }} />
              <Tooltip contentStyle={{ background: isDark ? "#0f172a" : "#fff", border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }} />
              <Bar yAxisId="left" dataKey="share" name="Volume Share %" fill={palette[2]} radius={[6, 6, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="approval_rate" name="Approval %" stroke={palette[0]} strokeWidth={2} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}

// ============ Denial Analytics ============
function DenialSection({ data, isDark, palette }) {
  if (!data) return <SectionLoader />;
  const surfaceCard = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-gray-200";
  const textTitle = isDark ? "text-white" : "text-gray-900";
  const axisColor = isDark ? "#64748b" : "#475569";

  return (
    <div className="space-y-6" data-testid="section-denials">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Total Denials YTD</span>
          <div className={`text-3xl font-bold mt-2 ${textTitle}`}>{fmtNumber(data.total_denials_ytd)}</div>
        </CardContent></Card>
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Avg Overturn Rate</span>
          <div className={`text-3xl font-bold mt-2 ${textTitle}`}>{fmtPercent(data.avg_overturn_rate)}</div>
        </CardContent></Card>
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Top Reason</span>
          <div className={`text-base font-semibold mt-2 ${textTitle}`}>{data.reasons[0]?.reason}</div>
        </CardContent></Card>
      </div>

      <Card className={surfaceCard}>
        <CardHeader><CardTitle className={`text-base ${textTitle}`}>Top Denial Reasons</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={data.reasons} layout="vertical" margin={{ left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1e293b" : "#e2e8f0"} />
              <XAxis type="number" tick={{ fill: axisColor, fontSize: 11 }} />
              <YAxis dataKey="reason" type="category" width={220} tick={{ fill: axisColor, fontSize: 11 }} />
              <Tooltip contentStyle={{ background: isDark ? "#0f172a" : "#fff", border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }} />
              <Bar dataKey="count" fill={palette[4]} radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className={surfaceCard}>
          <CardHeader><CardTitle className={`text-base ${textTitle}`}>Denials by Payer</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.by_payer}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1e293b" : "#e2e8f0"} />
                <XAxis dataKey="payer" tick={{ fill: axisColor, fontSize: 10 }} />
                <YAxis tick={{ fill: axisColor, fontSize: 11 }} />
                <Tooltip contentStyle={{ background: isDark ? "#0f172a" : "#fff", border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }} />
                <Bar dataKey="denials" fill={palette[3]} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className={surfaceCard}>
          <CardHeader><CardTitle className={`text-base ${textTitle}`}>Denial Trend & Appeal Outcomes</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={data.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1e293b" : "#e2e8f0"} />
                <XAxis dataKey="month" tick={{ fill: axisColor, fontSize: 10 }} />
                <YAxis tick={{ fill: axisColor, fontSize: 11 }} />
                <Tooltip contentStyle={{ background: isDark ? "#0f172a" : "#fff", border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }} />
                <Line type="monotone" dataKey="denied" stroke={palette[4]} strokeWidth={2} />
                <Line type="monotone" dataKey="appealed" stroke={palette[3]} strokeWidth={2} />
                <Line type="monotone" dataKey="overturned" stroke={palette[0]} strokeWidth={2} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card className={surfaceCard}>
        <CardHeader><CardTitle className={`text-base ${textTitle}`}>Denial Reason Cost Impact</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${isDark ? "border-slate-800 text-slate-400" : "border-gray-200 text-gray-600"}`}>
                  <th className="text-left py-2 px-3">Reason</th>
                  <th className="text-right py-2 px-3">Count</th>
                  <th className="text-right py-2 px-3">Appeal Success</th>
                  <th className="text-right py-2 px-3">Avg Cost Impact</th>
                </tr>
              </thead>
              <tbody>
                {data.reasons.map((r, i) => (
                  <tr key={i} className={`border-b ${isDark ? "border-slate-800/40 text-slate-300" : "border-gray-100 text-gray-700"}`}>
                    <td className="py-2 px-3">{r.reason}</td>
                    <td className="text-right py-2 px-3 font-mono">{fmtNumber(r.count)}</td>
                    <td className="text-right py-2 px-3 font-mono">{fmtPercent(r.avg_appeal_success)}</td>
                    <td className="text-right py-2 px-3 font-mono">{fmtCurrency(r.avg_cost_impact)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============ Claims Intelligence ============
function ClaimsSection({ data, isDark, palette }) {
  if (!data) return <SectionLoader />;
  const surfaceCard = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-gray-200";
  const textTitle = isDark ? "text-white" : "text-gray-900";
  const axisColor = isDark ? "#64748b" : "#475569";

  return (
    <div className="space-y-6" data-testid="section-claims">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Clean Claim Rate</span>
          <div className={`text-2xl font-bold mt-2 ${textTitle}`}>{fmtPercent(data.kpis.clean_claim_rate)}</div>
        </CardContent></Card>
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Paid $</span>
          <div className={`text-2xl font-bold mt-2 ${textTitle}`}>{fmtCurrency(data.kpis.paid_dollars)}</div>
        </CardContent></Card>
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Denial $</span>
          <div className={`text-2xl font-bold mt-2 ${textTitle}`}>{fmtCurrency(data.kpis.denial_dollars)}</div>
        </CardContent></Card>
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>A/R Days</span>
          <div className={`text-2xl font-bold mt-2 ${textTitle}`}>{data.kpis.ar_days.toFixed(1)}</div>
        </CardContent></Card>
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>First-Pass</span>
          <div className={`text-2xl font-bold mt-2 ${textTitle}`}>{fmtPercent(data.kpis.first_pass_resolution)}</div>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className={surfaceCard}>
          <CardHeader><CardTitle className={`text-base ${textTitle}`}>Top CPT — Submitted vs Paid vs Denied</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={data.top_cpts}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1e293b" : "#e2e8f0"} />
                <XAxis dataKey="cpt" tick={{ fill: axisColor, fontSize: 10 }} />
                <YAxis tick={{ fill: axisColor, fontSize: 11 }} />
                <Tooltip contentStyle={{ background: isDark ? "#0f172a" : "#fff", border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }} />
                <Bar dataKey="submitted" stackId="a" fill={palette[6]} />
                <Bar dataKey="paid" stackId="a" fill={palette[0]} />
                <Bar dataKey="denied" stackId="a" fill={palette[4]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className={surfaceCard}>
          <CardHeader><CardTitle className={`text-base ${textTitle}`}>A/R Aging Buckets</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={data.ar_buckets} layout="vertical" margin={{ left: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1e293b" : "#e2e8f0"} />
                <XAxis type="number" tick={{ fill: axisColor, fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <YAxis dataKey="bucket" type="category" width={90} tick={{ fill: axisColor, fontSize: 11 }} />
                <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={{ background: isDark ? "#0f172a" : "#fff", border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }} />
                <Bar dataKey="amount" fill={palette[2]} radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card className={surfaceCard}>
        <CardHeader><CardTitle className={`text-base ${textTitle}`}>CPT Performance Table</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${isDark ? "border-slate-800 text-slate-400" : "border-gray-200 text-gray-600"}`}>
                  <th className="text-left py-2 px-3">CPT</th>
                  <th className="text-left py-2 px-3">Description</th>
                  <th className="text-right py-2 px-3">Submitted</th>
                  <th className="text-right py-2 px-3">Paid</th>
                  <th className="text-right py-2 px-3">Denied</th>
                  <th className="text-right py-2 px-3">Avg Paid</th>
                </tr>
              </thead>
              <tbody>
                {data.top_cpts.map((c, i) => (
                  <tr key={i} className={`border-b ${isDark ? "border-slate-800/40 text-slate-300" : "border-gray-100 text-gray-700"}`}>
                    <td className="py-2 px-3 font-mono">{c.cpt}</td>
                    <td className="py-2 px-3">{c.description}</td>
                    <td className="text-right py-2 px-3 font-mono">{fmtNumber(c.submitted)}</td>
                    <td className="text-right py-2 px-3 font-mono text-emerald-500">{fmtNumber(c.paid)}</td>
                    <td className="text-right py-2 px-3 font-mono text-rose-500">{fmtNumber(c.denied)}</td>
                    <td className="text-right py-2 px-3 font-mono">{fmtCurrency(c.avg_paid_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============ Referral Analytics ============
function ReferralSection({ data, isDark, palette }) {
  if (!data) return <SectionLoader />;
  const surfaceCard = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-gray-200";
  const textTitle = isDark ? "text-white" : "text-gray-900";
  const axisColor = isDark ? "#64748b" : "#475569";

  return (
    <div className="space-y-6" data-testid="section-referrals">
      <Card className={surfaceCard}>
        <CardHeader><CardTitle className={`text-base ${textTitle}`}>Referrals by Specialty</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={data.by_specialty}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1e293b" : "#e2e8f0"} />
              <XAxis dataKey="specialty" tick={{ fill: axisColor, fontSize: 10 }} />
              <YAxis tick={{ fill: axisColor, fontSize: 11 }} />
              <Tooltip contentStyle={{ background: isDark ? "#0f172a" : "#fff", border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }} />
              <Bar dataKey="inbound" fill={palette[0]} radius={[6, 6, 0, 0]} />
              <Bar dataKey="outbound" fill={palette[2]} radius={[6, 6, 0, 0]} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className={surfaceCard}>
          <CardHeader><CardTitle className={`text-base ${textTitle}`}>Conversion Funnel</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3 mt-2">
              {data.funnel.map((s, i) => {
                const max = data.funnel[0].count;
                const pct = (s.count / max) * 100;
                return (
                  <div key={i}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className={isDark ? "text-slate-300" : "text-gray-700"}>{s.stage}</span>
                      <span className={isDark ? "text-slate-400" : "text-gray-600"}>{fmtNumber(s.count)} ({pct.toFixed(0)}%)</span>
                    </div>
                    <div className="h-7 rounded-md bg-slate-700/20 overflow-hidden">
                      <div className="h-full transition-all" style={{ width: `${pct}%`, background: palette[i % palette.length] }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className={surfaceCard}>
          <CardHeader><CardTitle className={`text-base ${textTitle}`}>Referral Leakage</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.leakage.map((l, i) => (
                <div key={i} className={`flex items-center justify-between p-3 rounded-lg ${isDark ? "bg-slate-800/40" : "bg-gray-50"}`}>
                  <div>
                    <div className={`text-sm font-medium ${textTitle}`}>{l.specialty}</div>
                    <div className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Lost to: {l.lost_to}</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-mono ${textTitle}`}>{l.patients} pts</div>
                    <div className="text-xs text-rose-500">{fmtCurrency(l.revenue_impact)}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ============ Pharmacy ============
function PharmacySection({ data, isDark, palette }) {
  if (!data) return <SectionLoader />;
  const surfaceCard = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-gray-200";
  const textTitle = isDark ? "text-white" : "text-gray-900";
  const axisColor = isDark ? "#64748b" : "#475569";

  return (
    <div className="space-y-6" data-testid="section-pharmacy">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Total Pharmacy PA</span>
          <div className={`text-3xl font-bold mt-2 ${textTitle}`}>{fmtNumber(data.total_pharmacy_pa)}</div>
        </CardContent></Card>
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Specialty Pharmacy %</span>
          <div className={`text-3xl font-bold mt-2 ${textTitle}`}>{fmtPercent(data.specialty_pharmacy_pct)}</div>
        </CardContent></Card>
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Formulary Gaps</span>
          <div className={`text-3xl font-bold mt-2 ${textTitle}`}>{data.formulary_gaps.length}</div>
        </CardContent></Card>
      </div>

      <Card className={surfaceCard}>
        <CardHeader><CardTitle className={`text-base ${textTitle}`}>Pharmacy PA by Drug Class</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={data.by_class}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1e293b" : "#e2e8f0"} />
              <XAxis dataKey="drug_class" tick={{ fill: axisColor, fontSize: 10 }} />
              <YAxis yAxisId="left" tick={{ fill: axisColor, fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: axisColor, fontSize: 11 }} />
              <Tooltip contentStyle={{ background: isDark ? "#0f172a" : "#fff", border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }} />
              <Bar yAxisId="left" dataKey="requests" fill={palette[5]} radius={[6, 6, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="approval_rate" stroke={palette[0]} strokeWidth={2} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className={surfaceCard}>
        <CardHeader><CardTitle className={`text-base ${textTitle}`}>Formulary Gaps</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {data.formulary_gaps.map((g, i) => (
              <div key={i} className={`p-3 rounded-lg border ${isDark ? "border-amber-500/30 bg-amber-500/5" : "border-amber-200 bg-amber-50"}`}>
                <div className={`text-sm font-semibold ${textTitle}`}>{g.drug}</div>
                <div className={`text-xs ${isDark ? "text-slate-400" : "text-gray-600"} mt-1`}>{g.payer}</div>
                <div className="flex items-center justify-between mt-2">
                  <Badge variant="outline" className="text-xs">Tier {g.tier}</Badge>
                  <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>{g.alternatives} alts</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============ AI Predictions ============
function AiPredictionsSection({ data, isDark, palette, onExplain }) {
  if (!data) return <SectionLoader />;
  const surfaceCard = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-gray-200";
  const textTitle = isDark ? "text-white" : "text-gray-900";

  const bandColor = (band) => band === "high" ? "bg-rose-500/20 text-rose-400 border-rose-500/30"
    : band === "medium" ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
    : "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";

  return (
    <div className="space-y-6" data-testid="section-ai-predictions">
      <Card className={`${surfaceCard} border-l-4 border-l-purple-500`}>
        <CardContent className="p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20"><Brain className="w-5 h-5 text-purple-400" /></div>
            <div>
              <div className={`text-sm font-semibold ${textTitle}`}>Live Gemini 3 Flash AI Predictions</div>
              <div className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Click "Explain" on any PA for a live LLM denial-risk analysis</div>
            </div>
          </div>
          <Sparkles className="w-5 h-5 text-purple-400" />
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Avg Risk Score</span>
          <div className={`text-3xl font-bold mt-2 ${textTitle}`}>{data.summary.avg_risk}</div>
        </CardContent></Card>
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>High Risk</span>
          <div className="text-3xl font-bold mt-2 text-rose-500">{data.summary.high}</div>
        </CardContent></Card>
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Medium Risk</span>
          <div className="text-3xl font-bold mt-2 text-amber-500">{data.summary.medium}</div>
        </CardContent></Card>
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Low Risk</span>
          <div className="text-3xl font-bold mt-2 text-emerald-500">{data.summary.low}</div>
        </CardContent></Card>
      </div>

      <Card className={surfaceCard}>
        <CardHeader><CardTitle className={`text-base ${textTitle}`}>Recent PA Requests — Denial Risk</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${isDark ? "border-slate-800 text-slate-400" : "border-gray-200 text-gray-600"}`}>
                  <th className="text-left py-2 px-3">PA ID</th>
                  <th className="text-left py-2 px-3">Patient</th>
                  <th className="text-left py-2 px-3">Payer</th>
                  <th className="text-left py-2 px-3">Service</th>
                  <th className="text-center py-2 px-3">Risk</th>
                  <th className="text-left py-2 px-3">Top Factors</th>
                  <th className="text-right py-2 px-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((p, i) => (
                  <tr key={i} className={`border-b ${isDark ? "border-slate-800/40 text-slate-300" : "border-gray-100 text-gray-700"}`}
                    data-testid={`ai-pred-row-${p.pa_id}`}>
                    <td className="py-2 px-3 font-mono text-xs">{p.pa_id}</td>
                    <td className="py-2 px-3">{p.patient}</td>
                    <td className="py-2 px-3">{p.payer}</td>
                    <td className="py-2 px-3">{p.service}</td>
                    <td className="text-center py-2 px-3">
                      <Badge variant="outline" className={`${bandColor(p.risk_band)} font-mono`}>{p.risk_score}</Badge>
                    </td>
                    <td className="py-2 px-3 text-xs">
                      <ul className="list-disc list-inside space-y-0.5">
                        {p.top_factors.map((f, j) => <li key={j}>{f}</li>)}
                      </ul>
                    </td>
                    <td className="text-right py-2 px-3">
                      <Button size="sm" variant="outline" onClick={() => onExplain(p)}
                        data-testid={`explain-btn-${p.pa_id}`}>
                        <Sparkles className="w-3 h-3 mr-1" /> Explain
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============ Provider Productivity ============
function ProductivitySection({ data, isDark, palette }) {
  if (!data) return <SectionLoader />;
  const surfaceCard = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-gray-200";
  const textTitle = isDark ? "text-white" : "text-gray-900";
  const axisColor = isDark ? "#64748b" : "#475569";

  return (
    <div className="space-y-6" data-testid="section-productivity">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Team Approval</span>
          <div className={`text-2xl font-bold mt-2 ${textTitle}`}>{fmtPercent(data.team_avg.approval_rate)}</div>
        </CardContent></Card>
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Team Turnaround</span>
          <div className={`text-2xl font-bold mt-2 ${textTitle}`}>{fmtHours(data.team_avg.avg_turnaround_hrs)}</div>
        </CardContent></Card>
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>First-Pass Avg</span>
          <div className={`text-2xl font-bold mt-2 ${textTitle}`}>{fmtPercent(data.team_avg.first_pass_rate)}</div>
        </CardContent></Card>
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Completeness Avg</span>
          <div className={`text-2xl font-bold mt-2 ${textTitle}`}>{fmtPercent(data.team_avg.completeness_score)}</div>
        </CardContent></Card>
      </div>

      <Card className={surfaceCard}>
        <CardHeader><CardTitle className={`text-base ${textTitle}`}>Provider Productivity Leaderboard</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${isDark ? "border-slate-800 text-slate-400" : "border-gray-200 text-gray-600"}`}>
                  <th className="text-left py-2 px-3">Provider</th>
                  <th className="text-left py-2 px-3">Specialty</th>
                  <th className="text-right py-2 px-3">Volume</th>
                  <th className="text-right py-2 px-3">Approval %</th>
                  <th className="text-right py-2 px-3">Turnaround</th>
                  <th className="text-right py-2 px-3">First-Pass</th>
                  <th className="text-right py-2 px-3">Completeness</th>
                </tr>
              </thead>
              <tbody>
                {data.providers.map((p, i) => (
                  <tr key={i} className={`border-b ${isDark ? "border-slate-800/40 text-slate-300" : "border-gray-100 text-gray-700"}`}>
                    <td className="py-2 px-3">
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs font-mono opacity-70">{p.npi}</div>
                    </td>
                    <td className="py-2 px-3">{p.specialty}</td>
                    <td className="text-right py-2 px-3 font-mono">{p.pa_volume}</td>
                    <td className="text-right py-2 px-3 font-mono text-emerald-500">{fmtPercent(p.approval_rate)}</td>
                    <td className="text-right py-2 px-3 font-mono">{fmtHours(p.avg_turnaround_hrs)}</td>
                    <td className="text-right py-2 px-3 font-mono">{fmtPercent(p.first_pass_rate)}</td>
                    <td className="text-right py-2 px-3 font-mono">{fmtPercent(p.completeness_score)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============ Multi-Location Enterprise ============
function EnterpriseSection({ data, isDark, palette }) {
  if (!data) return <SectionLoader />;
  const surfaceCard = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-gray-200";
  const textTitle = isDark ? "text-white" : "text-gray-900";
  const axisColor = isDark ? "#64748b" : "#475569";

  return (
    <div className="space-y-6" data-testid="section-enterprise">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Locations</span>
          <div className={`text-3xl font-bold mt-2 ${textTitle}`}>{data.total_locations}</div>
        </CardContent></Card>
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Total Providers</span>
          <div className={`text-3xl font-bold mt-2 ${textTitle}`}>{data.total_providers}</div>
        </CardContent></Card>
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Enterprise Approval</span>
          <div className={`text-3xl font-bold mt-2 ${textTitle}`}>{fmtPercent(data.enterprise_approval_rate)}</div>
        </CardContent></Card>
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Regions</span>
          <div className={`text-3xl font-bold mt-2 ${textTitle}`}>{data.regions.length}</div>
        </CardContent></Card>
      </div>

      <Card className={surfaceCard}>
        <CardHeader><CardTitle className={`text-base ${textTitle}`}>Location Performance</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.locations.map((l, i) => (
              <div key={i} className={`p-4 rounded-lg border ${isDark ? "border-slate-800 bg-slate-800/30" : "border-gray-200 bg-gray-50"}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className={`text-sm font-semibold ${textTitle}`}>{l.name}</div>
                    <div className={`text-xs flex items-center gap-1 ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                      <MapPin className="w-3 h-3" />{l.city}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs">{l.providers} providers</Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <div>
                    <div className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>PA Volume</div>
                    <div className={`text-sm font-mono ${textTitle}`}>{fmtNumber(l.pa_volume)}</div>
                  </div>
                  <div>
                    <div className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Approval</div>
                    <div className="text-sm font-mono text-emerald-500">{fmtPercent(l.approval_rate)}</div>
                  </div>
                  <div>
                    <div className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Turnaround</div>
                    <div className={`text-sm font-mono ${textTitle}`}>{fmtHours(l.avg_turnaround_hrs)}</div>
                  </div>
                  <div>
                    <div className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Revenue Protected</div>
                    <div className="text-sm font-mono text-emerald-500">{fmtCurrency(l.revenue_protected)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className={surfaceCard}>
        <CardHeader><CardTitle className={`text-base ${textTitle}`}>Regional Comparison</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={data.regions}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1e293b" : "#e2e8f0"} />
              <XAxis dataKey="region" tick={{ fill: axisColor, fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fill: axisColor, fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: axisColor, fontSize: 11 }} />
              <Tooltip contentStyle={{ background: isDark ? "#0f172a" : "#fff", border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }} />
              <Bar yAxisId="left" dataKey="pa_volume" fill={palette[1]} radius={[6, 6, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="approval_rate" stroke={palette[0]} strokeWidth={2} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}

// ============ NPI Portability ============
function NpiPortabilitySection({ data, isDark, palette }) {
  if (!data) return <SectionLoader />;
  const surfaceCard = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-gray-200";
  const textTitle = isDark ? "text-white" : "text-gray-900";
  const axisColor = isDark ? "#64748b" : "#475569";

  return (
    <div className="space-y-6" data-testid="section-npi-portability">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Portability Score</span>
          <div className={`text-3xl font-bold mt-2 ${textTitle}`}>{data.portability_score}</div>
        </CardContent></Card>
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>Active NPIs</span>
          <div className={`text-3xl font-bold mt-2 ${textTitle}`}>{fmtNumber(data.active_npis)}</div>
        </CardContent></Card>
        <Card className={surfaceCard}><CardContent className="p-5">
          <span className={`text-xs uppercase ${isDark ? "text-slate-400" : "text-gray-500"}`}>New This Quarter</span>
          <div className="text-3xl font-bold mt-2 text-emerald-500">+{data.new_this_quarter}</div>
        </CardContent></Card>
      </div>

      <Card className={surfaceCard}>
        <CardHeader><CardTitle className={`text-base ${textTitle}`}>Provider Churn — Joined vs Left</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.churn_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1e293b" : "#e2e8f0"} />
              <XAxis dataKey="month" tick={{ fill: axisColor, fontSize: 10 }} />
              <YAxis tick={{ fill: axisColor, fontSize: 11 }} />
              <Tooltip contentStyle={{ background: isDark ? "#0f172a" : "#fff", border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }} />
              <Bar dataKey="joined" stackId="a" fill={palette[0]} />
              <Bar dataKey="left" stackId="b" fill={palette[4]} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className={surfaceCard}>
        <CardHeader><CardTitle className={`text-base ${textTitle}`}>Recent NPI Movements</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${isDark ? "border-slate-800 text-slate-400" : "border-gray-200 text-gray-600"}`}>
                  <th className="text-left py-2 px-3">From</th>
                  <th className="text-left py-2 px-3">To</th>
                  <th className="text-left py-2 px-3">Specialty</th>
                  <th className="text-right py-2 px-3">Providers</th>
                  <th className="text-right py-2 px-3">Approval Δ</th>
                </tr>
              </thead>
              <tbody>
                {data.flows.map((f, i) => (
                  <tr key={i} className={`border-b ${isDark ? "border-slate-800/40 text-slate-300" : "border-gray-100 text-gray-700"}`}>
                    <td className="py-2 px-3">{f.from_org}</td>
                    <td className="py-2 px-3 flex items-center gap-1"><ChevronRight className="w-3 h-3" />{f.to_org}</td>
                    <td className="py-2 px-3">{f.specialty}</td>
                    <td className="text-right py-2 px-3 font-mono">{f.providers_moved}</td>
                    <td className={`text-right py-2 px-3 font-mono ${f.approval_rate_change >= 0 ? "text-emerald-500" : "text-rose-500"}`}>
                      {f.approval_rate_change >= 0 ? "+" : ""}{f.approval_rate_change}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SectionLoader() {
  return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>;
}

// ============ Explain Dialog ============
function ExplainDialog({ open, onClose, pa, isDark }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open || !pa) return;
    setLoading(true); setResult(null);
    axios.post(`${API}/insights/ai-explain/${pa.pa_id}`, {}, { withCredentials: true })
      .then(r => setResult(r.data))
      .catch(e => toast.error(e.response?.data?.detail || "AI explain failed"))
      .finally(() => setLoading(false));
  }, [open, pa]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={isDark ? "bg-slate-900 border-slate-800 text-white max-w-2xl" : "max-w-2xl"}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-400" /> AI Denial-Risk Analysis
          </DialogTitle>
          <DialogDescription>
            {pa?.pa_id} — {pa?.patient} ({pa?.payer})
          </DialogDescription>
        </DialogHeader>
        {loading && <div className="py-10 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
          <span className="ml-3 text-sm">Calling Gemini 3 Flash...</span>
        </div>}
        {result && !loading && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className={`p-4 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
                <div className="text-xs opacity-70">Risk Score</div>
                <div className="text-2xl font-bold">{result.risk_score}</div>
              </div>
              <div className={`p-4 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
                <div className="text-xs opacity-70">Denial Probability</div>
                <div className="text-2xl font-bold">{result.denial_probability_pct}%</div>
              </div>
              <div className={`p-4 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
                <div className="text-xs opacity-70">Band</div>
                <Badge className={`mt-1 ${result.risk_band === "high" ? "bg-rose-500" : result.risk_band === "medium" ? "bg-amber-500" : "bg-emerald-500"}`}>
                  {result.risk_band?.toUpperCase()}
                </Badge>
              </div>
            </div>
            <div>
              <div className="text-sm font-semibold mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" /> Key Risk Factors</div>
              <ul className="text-sm space-y-1 list-disc list-inside opacity-90">
                {(result.key_risk_factors || []).map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>
            <div>
              <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Zap className="w-4 h-4 text-emerald-400" /> Recommended Actions</div>
              <ul className="text-sm space-y-1 list-disc list-inside opacity-90">
                {(result.recommended_actions || []).map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>
            {result.summary && (
              <div className={`p-3 rounded-lg text-sm ${isDark ? "bg-purple-500/10 border border-purple-500/30" : "bg-purple-50 border border-purple-200"}`}>
                <div className="text-xs font-semibold opacity-70 mb-1">SUMMARY</div>
                {result.summary}
              </div>
            )}
            <div className="text-xs opacity-60 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> {result.powered_by}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============ Main Component ============
export default function PriorAuthInsights() {
  return (
    <DashboardLayout>
      <PriorAuthInsightsInner />
    </DashboardLayout>
  );
}

function PriorAuthInsightsInner() {
  const { isDark } = useTheme();
  const [active, setActive] = useState("executive");
  const [cache, setCache] = useState({});
  const [loadingSection, setLoadingSection] = useState(false);
  const [explainPa, setExplainPa] = useState(null);
  const palette = isDark ? PALETTE_DARK : PALETTE_LIGHT;

  const fetchSection = async (id) => {
    if (cache[id]) return;
    setLoadingSection(true);
    try {
      const r = await axios.get(`${API}/insights/${id}`, { withCredentials: true });
      setCache(prev => ({ ...prev, [id]: r.data }));
    } catch (e) {
      toast.error(`Failed to load ${id}`);
    } finally {
      setLoadingSection(false);
    }
  };

  useEffect(() => { fetchSection(active); /* eslint-disable-next-line */ }, [active]);

  const renderSection = () => {
    const data = cache[active];
    switch (active) {
      case "executive": return <ExecutiveSection data={data} isDark={isDark} palette={palette} />;
      case "denials": return <DenialSection data={data} isDark={isDark} palette={palette} />;
      case "claims": return <ClaimsSection data={data} isDark={isDark} palette={palette} />;
      case "referrals": return <ReferralSection data={data} isDark={isDark} palette={palette} />;
      case "pharmacy": return <PharmacySection data={data} isDark={isDark} palette={palette} />;
      case "ai-predictions": return <AiPredictionsSection data={data} isDark={isDark} palette={palette} onExplain={setExplainPa} />;
      case "productivity": return <ProductivitySection data={data} isDark={isDark} palette={palette} />;
      case "enterprise": return <EnterpriseSection data={data} isDark={isDark} palette={palette} />;
      case "npi-portability": return <NpiPortabilitySection data={data} isDark={isDark} palette={palette} />;
      default: return null;
    }
  };

  const headerTitle = isDark ? "text-white" : "text-gray-900";
  const headerSub = isDark ? "text-slate-400" : "text-gray-500";
  const sidebarCard = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-gray-200";

  return (
    <div data-testid="priorath-insights-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-5 h-5 text-purple-400" />
          <Badge variant="outline" className="text-xs">PriorAuth Pro</Badge>
        </div>
        <h1 className={`text-3xl font-bold ${headerTitle}`}>Analytics & Insights Dashboard</h1>
        <p className={`text-sm ${headerSub} mt-1`}>
          10-section deep-dive across PA volume, denials, claims, referrals, pharmacy, AI risk, productivity & enterprise.
        </p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left rail nav */}
        <aside className="col-span-12 lg:col-span-3 xl:col-span-2">
          <Card className={`${sidebarCard} sticky top-20`}>
            <CardContent className="p-3">
              <nav className="space-y-1">
                {SECTIONS.map(s => {
                  const isActive = s.id === active;
                  return (
                    <button key={s.id}
                      data-testid={`insights-nav-${s.id}`}
                      onClick={() => setActive(s.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left transition-colors ${
                        isActive
                          ? (isDark ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-blue-50 text-[#0066a1] border border-blue-200")
                          : (isDark ? "text-slate-400 hover:bg-slate-800 hover:text-white" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900")
                      }`}>
                      <s.icon className="w-4 h-4 shrink-0" />
                      <span className="truncate">{s.label}</span>
                      {isActive && <ChevronRight className="w-3 h-3 ml-auto" />}
                    </button>
                  );
                })}
              </nav>
            </CardContent>
          </Card>
        </aside>

        {/* Main content */}
        <div className="col-span-12 lg:col-span-9 xl:col-span-10">
          {loadingSection && !cache[active] ? <SectionLoader /> : renderSection()}
        </div>
      </div>

      <ExplainDialog open={!!explainPa} onClose={() => setExplainPa(null)} pa={explainPa} isDark={isDark} />
    </div>
  );
}
